import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LockedOverlay } from "@/components/LockedOverlay";
import { Sparkles, RefreshCw } from "lucide-react";

export function AnalysisCard({ refreshKey }) {
  const { isPro } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const r = await api.get(`/analysis${refresh ? "?refresh=true" : ""}`);
      setData(r.data);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isPro) load(false); }, [load, refreshKey, isPro]);

  return (
    <section data-testid="ai-analysis-card" className="glass rounded-3xl p-5 relative overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[#F59E0B]" />
          <h2 className="font-display text-lg font-bold">AI Market Analysis</h2>
          <span className="text-[9px] font-mono-num uppercase tracking-wider text-[#6B7280] border border-white/10 rounded px-1.5 py-0.5">Claude</span>
        </div>
        {isPro && (
          <button data-testid="refresh-analysis" onClick={() => load(true)}
            className="w-8 h-8 rounded-lg grid place-items-center bg-white/5 hover:bg-white/10 border border-white/10">
            <RefreshCw size={13} className={`text-[#9CA3AF] ${loading ? "animate-spin" : ""}`} />
          </button>
        )}
      </div>

      {!isPro ? (
        <>
          <p className="text-sm text-[#6B7280] leading-relaxed blur-[3px] select-none">
            The engine's full macro thesis, Fed-path read, real-yield and DXY transmission, the key
            invalidation risk, and the exact execution line are generated here by Claude for every signal update…
          </p>
          <LockedOverlay label="Unlock AI Analysis" />
        </>
      ) : loading && !data ? (
        <div className="space-y-2">
          <div className="h-3 bg-white/5 rounded animate-pulse" />
          <div className="h-3 bg-white/5 rounded animate-pulse w-11/12" />
          <div className="h-3 bg-white/5 rounded animate-pulse w-10/12" />
        </div>
      ) : (
        <p className="text-sm text-[#D1D5DB] leading-relaxed whitespace-pre-line">{data?.text}</p>
      )}
    </section>
  );
}
